import styled from "styled-components";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";

import VideoGuide from "@/components/VideoGuide";
import { Modal } from "@/components/Modal";
import routes from "@/utils/routes";
import { RootState } from "@/utils/store";
import { ViewGuideResource } from "@/types/resources";
import { api } from "@/utils/api";
import useCurrentPathKey from "@/hooks/useCurrentPathKey";

export const GENERAL_GUIDE_KEY = "general";

const Info = styled.button`
  position: fixed;
  right: 24px;
  bottom: 24px;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.purpleLighter};
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1;
  transition: 0.2s ease-out all;
  &:hover,
  &:active {
    background-color: ${({ theme }) => theme.colors.purple};
  }
`;

const VideoGuideModal = () => {
  const [isInfoOpen, setIsInfoOpen] = useState(false);

  const currentPathKey = useCurrentPathKey();

  const wasVideoShown = useSelector((rootState: RootState) =>
    currentPathKey ? rootState.viewGuides[currentPathKey] : false
  );

  const paths = routes
    .filter((route) => route.title)
    .map((route) => route.path);

  const {
    data: viewGuides,
    isError,
    isLoading,
  } = useQuery<ViewGuideResource[]>(
    ["view_guides"],
    () => api.get<never, ViewGuideResource[]>(`/v1/view-guides`),
    {
      staleTime: 120 * 1000,
    }
  );

  const generalLink = viewGuides?.find(
    (guide) => guide.key === GENERAL_GUIDE_KEY
  )?.link;

  const currentGuide = viewGuides?.find(
    (guide) => guide.key === currentPathKey
  );

  const shouldRender =
    paths.includes(currentPathKey) &&
    !isError &&
    !isLoading &&
    !!currentGuide?.link;

  if (!currentPathKey) return null;

  if (!shouldRender) return null;

  if (!wasVideoShown) {
    return (
      <Modal>
        <VideoGuide
          isBig
          onClose={() => setIsInfoOpen(false)}
          currentPath={currentPathKey}
          currentGuide={currentGuide}
          generalLink={generalLink}
        />
      </Modal>
    );
  }

  return (
    <>
      <Info
        onClick={(e) => {
          e.stopPropagation();
          setIsInfoOpen((prev) => !prev);
        }}
      >
        {isInfoOpen ? (
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M7.28214 14.0712C6.89765 13.6747 6.90735 13.0416 7.30381 12.6571C7.70028 12.2726 8.33337 12.2823 8.71786 12.6788L11 15.032L11 6.5C11 5.94772 11.4477 5.5 12 5.5C12.5523 5.5 13 5.94772 13 6.5L13 15.032L15.2821 12.6788C15.6666 12.2823 16.2997 12.2726 16.6962 12.6571C17.0927 13.0416 17.1024 13.6747 16.7179 14.0712L12.7179 18.1957C12.5295 18.3899 12.2705 18.4995 12 18.4995C11.7295 18.4995 11.4705 18.3899 11.2821 18.1957L7.28214 14.0712Z"
              fill="white"
            />
          </svg>
        ) : (
          <svg
            width="28"
            height="28"
            viewBox="0 0 28 28"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M13.9986 24.2857C19.6792 24.2857 24.2843 19.6806 24.2843 14C24.2843 8.31934 19.6792 3.71426 13.9986 3.71426C8.31796 3.71426 3.71289 8.31934 3.71289 14C3.71289 19.6806 8.31796 24.2857 13.9986 24.2857Z"
              fill="white"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M13.1426 13.1429H13.9997V19.1429H14.8569"
              stroke="#5D54D6"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M13.9986 10.5714C14.7087 10.5714 15.2843 9.99579 15.2843 9.28571C15.2843 8.57563 14.7087 8 13.9986 8C13.2885 8 12.7129 8.57563 12.7129 9.28571C12.7129 9.99579 13.2885 10.5714 13.9986 10.5714Z"
              fill="#5D54D6"
            />
          </svg>
        )}
      </Info>
      {isInfoOpen && (
        <VideoGuide
          onClose={() => setIsInfoOpen(false)}
          currentPath={currentPathKey}
          currentGuide={currentGuide}
          generalLink={generalLink}
        />
      )}
    </>
  );
};

export default VideoGuideModal;
